// src/pages/Admin/DescuentosAutomaticos/DescuentosAutomaticosTable.jsx
import React, { useState, useEffect } from 'react';
import { 
  Table, Input, Select, Button, Tag, Space, Card, Alert,
  Tooltip, Typography, Statistic, App 
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, BarChartOutlined, CheckCircleOutlined,
  CloseCircleOutlined, PercentageOutlined, DollarOutlined,
  CalendarOutlined, InfoCircleOutlined, ThunderboltOutlined, DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import DescuentosAutomaticos from '../../../../service/CodigosDescuento.js';
import { useNotify, useWithNotify } from '../../../../providers/NotificationProvider.jsx';
import useAuthContext from '../../../../hooks/useAuthContext';
import { exportarHojaUnica } from '../../../../utils/exportExcel';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const estadoColor = {
  activo: '#0047bb',
  inactivo: '#ff4d4f',
};

const tipoColor = {
  porcentaje: '#0047bb',
  monto_fijo: '#0047bb'
};

const DescuentosAutomaticosTable = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();
  const [descuentos, setDescuentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [vigenciaFilter, setVigenciaFilter] = useState('all');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Cargar descuentos
  const loadDescuentos = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        ...(estadoFilter !== 'all' && { activo: estadoFilter === 'activo' }),
        ...(vigenciaFilter !== 'all' && { vigente: vigenciaFilter === 'vigente' })
      };
      
      const res = await DescuentosAutomaticos.List(params);

      setDescuentos(res.data || []);
      setPagination({
        current: res.pagination?.page || 1,
        pageSize: res.pagination?.limit || 10,
        total: res.pagination?.total || 0
      });

    } catch (error) {
      notify.error(error.message || 'Error al cargar descuentos automáticos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDescuentos(pagination.current, pagination.pageSize);
  }, [estadoFilter, vigenciaFilter]);

  // Verificar vigencia
  const esVigente = (descuento) => {
    const ahora = new Date();
    return descuento.activo && 
           new Date(descuento.fechaInicio) <= ahora && 
           new Date(descuento.fechaFin) >= ahora &&
           !descuento.eliminadoEn;
  };

  // Filtrado local (búsqueda y tipo)
  const filteredData = descuentos.filter(descuento => {
    const matchSearch = descuento.nombre.toLowerCase().includes(searchText.toLowerCase())
      || (descuento.descripcion || '').toLowerCase().includes(searchText.toLowerCase());
    const matchTipo = tipoFilter === 'all' || descuento.tipoDescuento === tipoFilter;
    
    return matchSearch && matchTipo;
  });

  // Eliminar descuento
  const handleDelete = async (id, nombre) => {
    modal.confirm({
      title: '¿Eliminar descuento automático?',
      content: (
        <div>
          <p>Estás a punto de eliminar el descuento <strong>{nombre}</strong>.</p>
          <p className="text-gray-500">Esta acción marcará el descuento como eliminado y dejará de aplicarse automáticamente.</p>
        </div>
      ),
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      onOk: async () => {
        try {
          await withNotify(
            DescuentosAutomaticos.Delete(id),
            {
              loading: 'Eliminando descuento...',
              success: 'Descuento eliminado exitosamente',
              error: 'Error al eliminar descuento'
            }
          );
          loadDescuentos(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error('Error eliminando descuento:', error);
        }
      }
    });
  };

  // Toggle activo
  const handleToggle = async (id, nombre, activo) => {
    const accion = activo ? 'desactivar' : 'activar';
    
    modal.confirm({
      title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} descuento?`,
      content: (
        <div>
          <p>¿Deseas {accion} el descuento <strong>{nombre}</strong>?</p>
          {activo ? (
            <p className="text-gray-500">El descuento dejará de aplicarse automáticamente.</p>
          ) : (
            <p className="text-gray-500">El descuento se aplicará automáticamente a los talleres configurados.</p>
          )}
        </div>
      ),
      okText: accion.charAt(0).toUpperCase() + accion.slice(1),
      okType: activo ? 'danger' : 'primary',
      cancelText: 'Cancelar',
      icon: activo ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> : <CheckCircleOutlined style={{ color: '#0047bb' }} />,
      onOk: async () => {
        try {
          await withNotify(
            DescuentosAutomaticos.ToggleActivo(id),
            {
              loading: 'Actualizando estado...',
              success: 'Estado actualizado exitosamente',
              error: 'Error al actualizar estado'
            }
          );
          loadDescuentos(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error('Error al cambiar estado:', error);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Descuento',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (text, record) => (
        <div>
          <div className="font-bold text-gray-900 text-base flex items-center gap-2">
            <ThunderboltOutlined className="text-yellow-500" />
            {text}
          </div>
          <div className="text-xs text-gray-500 mt-1">{record.descripcion}</div>
          {!record.activo && (
            <Tag color="red" className="mt-1">INACTIVO</Tag>
          )}
          {record.eliminadoEn && (
            <Tag color="default" className="mt-1">ELIMINADO</Tag>
          )}
        </div>
      ),
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
    },
    {
      title: 'Tipo / Valor',
      key: 'tipo',
      align: 'center',
      render: (_, record) => (
        <div>
          <Tag 
            color={tipoColor[record.tipoDescuento]} 
            icon={record.tipoDescuento === 'porcentaje' ? <PercentageOutlined /> : <DollarOutlined />}
            className="text-base font-semibold"
          >
            {record.tipoDescuento === 'porcentaje' 
              ? `${record.valorDescuento}%`
              : `$${record.valorDescuento.toLocaleString('es-CL')}`
            }
          </Tag>
          <div className="text-xs text-gray-500 mt-1">
            {record.tipoDescuento === 'porcentaje' ? 'Descuento porcentual' : 'Monto fijo'}
          </div>
          {record.acumulableConOtrosDescuentos && (
            <Tag color="purple" className="mt-1" style={{ fontSize: '10px' }}>
              Acumulable
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Vigencia',
      key: 'vigencia',
      render: (_, record) => {
        const vigente = esVigente(record);
        const ahora = new Date();
        const iniciado = new Date(record.fechaInicio) <= ahora;
        const terminado = new Date(record.fechaFin) < ahora;
        
        let estado = 'PROGRAMADO';
        let color = 'blue';
        
        if (terminado) {
          estado = 'VENCIDO';
          color = 'default';
        } else if (vigente) {
          estado = 'VIGENTE';
          color = 'success';
        } else if (!iniciado) {
          estado = 'PROGRAMADO';
          color = 'processing';
        }
        
        return (
          <div className="text-xs">
            <div className="mb-1">
              <Tag color={color} icon={<CalendarOutlined />}>
                {estado}
              </Tag>
            </div>
            <div className="text-gray-600">
              <div>Inicio: <b>{dayjs(record.fechaInicio).format('DD/MM/YYYY HH:mm')}</b></div>
              <div>Fin: <b>{dayjs(record.fechaFin).format('DD/MM/YYYY HH:mm')}</b></div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Talleres Aplicables',
      key: 'talleres',
      render: (_, record) => (
        <div className="text-xs">
          {record.talleresAplicables && record.talleresAplicables.length > 0 ? (
            <div>
              <Tag color="cyan">{record.talleresAplicables.length} talleres</Tag>
              {record.talleresAplicables.length <= 3 ? (
                <div className="mt-1 space-y-1">
                  {record.talleresAplicables.map((taller, idx) => (
                    <div key={idx} className="text-gray-600">
                      • {taller.nombre || taller}
                    </div>
                  ))}
                </div>
              ) : (
                <Tooltip title={
                  <div>
                    {record.talleresAplicables.map((taller, idx) => (
                      <div key={idx}>• {taller.nombre || taller}</div>
                    ))}
                  </div>
                }>
                  <InfoCircleOutlined className="text-gray-400 ml-1 cursor-pointer" />
                </Tooltip>
              )}
            </div>
          ) : (
            <Tag>Sin talleres asignados</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Prioridad',
      key: 'prioridad',
      align: 'center',
      render: (_, record) => (
        <div>
          <div className="text-lg font-bold" style={{ 
            color: record.prioridad > 5 ? '#0047bb' : record.prioridad > 2 ? '#0047bb' : '#8c8c8c' 
          }}>
            {record.prioridad}
          </div>
          <div className="text-xs text-gray-500">
            {record.prioridad > 5 ? 'Alta' : record.prioridad > 2 ? 'Media' : 'Baja'}
          </div>
        </div>
      ),
      sorter: (a, b) => b.prioridad - a.prioridad,
    },
    {
      title: 'Estado',
      key: 'estado',
      align: 'center',
      render: (_, record) => (
        <div>
          <Tag 
            color={record.activo ? estadoColor.activo : estadoColor.inactivo}
            icon={record.activo ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          >
            {record.activo ? 'ACTIVO' : 'INACTIVO'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/admin/descuentos-automaticos/${record._id}`)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              icon={<EditOutlined />}
              type="primary"
              size="small"
              onClick={() => navigate(`/admin/descuentos-automaticos/editar/${record._id}`)}
              style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
            />
          </Tooltip>

          <Tooltip title={record.activo ? 'Desactivar' : 'Activar'}>
            <Button
              icon={record.activo ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              size="small"
              onClick={() => handleToggle(record._id, record.nombre, record.activo)}
              danger={record.activo}
              type={record.activo ? 'default' : 'primary'}
            />
          </Tooltip>

          {!record.eliminadoEn && (
            <Tooltip title="Eliminar">
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                onClick={() => handleDelete(record._id, record.nombre)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Estadísticas rápidas
  const stats = {
    total: pagination.total,
    activos: descuentos.filter(d => d.activo && esVigente(d)).length,
    programados: descuentos.filter(d => d.activo && new Date(d.fechaInicio) > new Date()).length,
    vencidos: descuentos.filter(d => new Date(d.fechaFin) < new Date()).length
  };

  const handleTableChange = (newPagination) => {
    loadDescuentos(newPagination.current, newPagination.pageSize);
  };

  const handleExportarExcel = async () => {
    if (!filteredData.length) return;
    try {
      await exportarHojaUnica({
        archivo: 'descuentos-automaticos',
        titulo: 'Descuentos automáticos',
        subtitulo: 'Reglas de descuento configuradas y su vigencia',
        usuario: user,
        filtros: {
          'Búsqueda': searchText || null,
          'Tipo': tipoFilter !== 'all' ? tipoFilter : 'Todos',
          'Estado': estadoFilter !== 'all' ? estadoFilter : 'Todos',
          'Vigencia': vigenciaFilter !== 'all' ? vigenciaFilter : 'Todas',
        },
        nombreHoja: 'Descuentos',
        columnas: [
          { header: 'Nombre', key: 'nombre', tipo: 'texto', ancho: 34 },
          { header: 'Descripción', key: 'descripcion', tipo: 'texto', ancho: 40, ajustarTexto: true },
          { header: 'Tipo', key: 'tipo', tipo: 'texto' },
          // El valor se separa en dos columnas porque son unidades distintas:
          // mezclarlas en una sola obliga a guardarlas como texto.
          { header: 'Valor (%)', key: 'valorPorcentaje', tipo: 'porcentaje' },
          { header: 'Valor ($)', key: 'valorMonto', tipo: 'moneda' },
          { header: 'Fecha de inicio', key: 'fechaInicio', tipo: 'fecha' },
          { header: 'Fecha de término', key: 'fechaFin', tipo: 'fecha' },
          { header: 'Estado', key: 'estado', tipo: 'texto' },
          {
            header: 'Vigente hoy', key: 'vigenteHoy', tipo: 'texto',
            resaltar: (d) => (d.vigenteHoy === 'Sí' ? 'ok' : 'alerta'),
          },
          { header: 'Prioridad', key: 'prioridad', tipo: 'numero' },
        ],
        filas: filteredData.map(d => {
          const esPorcentaje = d.tipoDescuento === 'porcentaje';
          return {
            nombre: d.nombre,
            descripcion: d.descripcion,
            tipo: esPorcentaje ? 'Porcentaje' : 'Monto fijo',
            valorPorcentaje: esPorcentaje ? d.valorDescuento : null,
            valorMonto: esPorcentaje ? null : d.valorDescuento,
            fechaInicio: d.fechaInicio,
            fechaFin: d.fechaFin,
            estado: d.activo ? 'Activo' : 'Inactivo',
            vigenteHoy: esVigente(d) ? 'Sí' : 'No',
            prioridad: d.prioridad,
          };
        }),
        notas: ['Un descuento vigente hoy está activo y dentro de su rango de fechas.'],
      });
    } catch { notify.error('Error al generar Excel'); }
  };

  return (
    <div className="min-h-screen">
      <Card className="shadow-lg rounded-lg">
        {/* Header */}
        <div className="mb-6">
          <Title level={3} className="!mb-2 flex items-center gap-2">
            <ThunderboltOutlined className="text-yellow-500" />
            Descuentos Automáticos
          </Title>
          <Text type="secondary">
            Gestiona descuentos que se aplican automáticamente por período de tiempo
          </Text>
        </div>

        {/* Alert informativo */}
        <Alert
          message="Descuentos automáticos"
          description="Estos descuentos se aplican automáticamente durante el período configurado sin necesidad de códigos promocionales."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          className="mb-6"
        />

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card size="small" className="bg-blue-50">
            <Statistic 
              title="Total Descuentos" 
              value={stats.total} 
              prefix={<InfoCircleOutlined />}
            />
          </Card>
          <Card size="small" className="bg-blue-50">
            <Statistic 
              title="Vigentes Ahora" 
              value={stats.activos} 
              prefix={<CheckCircleOutlined style={{ color: '#0047bb' }} />}
              valueStyle={{ color: '#0047bb' }}
            />
          </Card>
          <Card size="small" className="bg-purple-50">
            <Statistic 
              title="Programados" 
              value={stats.programados} 
              prefix={<CalendarOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
          <Card size="small" className="bg-orange-50">
            <Statistic 
              title="Vencidos" 
              value={stats.vencidos} 
              prefix={<CloseCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </div>

        {/* Filtros y búsqueda */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <Search
              placeholder="Buscar por nombre o descripción..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="w-full md:w-96"
            />
            <div className="flex gap-2 w-full md:w-auto">
              <Button
                icon={<DownloadOutlined />}
                size="large"
                onClick={handleExportarExcel}
                disabled={!filteredData.length}
                style={{ fontWeight: 600, borderRadius: 8 }}
              >
                Exportar Excel
              </Button>
              <Button
                icon={<PlusOutlined />}
                type="primary"
                size="large"
                onClick={() => navigate('/admin/descuentos-automaticos/crear')}
                style={{ backgroundColor: '#0047bb', borderColor: '#0047bb', fontWeight: 600 }}
                className="w-full md:w-auto"
              >
                Crear Descuento Automático
              </Button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Space size="middle" className="flex-wrap">
              <Select
                value={tipoFilter}
                onChange={setTipoFilter}
                style={{ minWidth: 180 }}
              >
                <Option value="all">Todos los tipos</Option>
                <Option value="porcentaje">Porcentaje</Option>
                <Option value="monto_fijo">Monto fijo</Option>
              </Select>
              
              <Select
                value={estadoFilter}
                onChange={setEstadoFilter}
                style={{ minWidth: 180 }}
              >
                <Option value="all">Todos los estados</Option>
                <Option value="activo">Activos</Option>
                <Option value="inactivo">Inactivos</Option>
              </Select>

              <Select
                value={vigenciaFilter}
                onChange={setVigenciaFilter}
                style={{ minWidth: 180 }}
              >
                <Option value="all">Todas las vigencias</Option>
                <Option value="vigente">Vigentes ahora</Option>
              </Select>
            </Space>
            
            <div className="flex items-center ml-auto">
              <Text type="secondary">
                Mostrando <Text strong style={{ color: '#0047bb' }}>{filteredData.length}</Text> de {pagination.total} descuentos
              </Text>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} descuentos`,
            pageSizeOptions: ['5', '10', '20', '50'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
          className="custom-table"
          rowClassName={(record) => {
            if (record.eliminadoEn) return 'bg-gray-100 opacity-60';
            if (!record.activo) return 'bg-red-50';
            if (esVigente(record)) return 'bg-blue-50 hover:bg-blue-100';
            if (new Date(record.fechaInicio) > new Date()) return 'bg-blue-50 hover:bg-blue-100';
            return 'hover:bg-gray-50';
          }}
        />
      </Card>

      <style jsx>{`
        .custom-table :global(.ant-table-thead > tr > th) {
          background-color: #f8fafc;
          color: #1f2937;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
        }
        .custom-table :global(.ant-pagination-item-active) {
          border-color: #0047bb;
          background-color: #0047bb;
        }
        .custom-table :global(.ant-pagination-item-active a) {
          color: white;
        }
      `}</style>
    </div>
  );
};

export default DescuentosAutomaticosTable;
